/**
 * learnerPseudonym.ts — de ENIGE plek waar een leerling-pseudoniem ontstaat.
 *
 * WAAROM DIT BESTAAT
 *
 * Een pseudoniem (`L-A3F91`) houdt namen van kinderen uit schermen en
 * exportbestanden die ze niet nodig hebben — het activiteitenlog meet of het
 * systeem gebruikt wordt en of scores plausibel zijn, en dat is een
 * aggregatievraag. De harde eis is dat hetzelfde kind ALTIJD hetzelfde label
 * krijgt: twee sessies van één kind moeten als één kind herkenbaar blijven,
 * anders is het log onbruikbaar voor analyse.
 *
 * Die eis gold tot 12 aug 2026 alleen per scherm. Er waren TWEE onafhankelijke
 * implementaties met verschillende algoritmes:
 *
 *   - `LogsPage.tsx`  — base-36 van een rolling hash
 *   - `admin_list_accounts()` (0025) — hex van een SHA-256-digest
 *
 * Allebei deterministisch, dus binnen hun eigen scherm klopte het. Maar
 * hetzelfde kind kreeg in het activiteitenlog een ánder label dan in de
 * accountlijst. Precies de eigenschap die het pseudoniem moet leveren, was
 * tussen twee schermen kwijt — en dat valt niet op door te kijken, want beide
 * labels zien er even geldig uit.
 *
 * DE KEUZE: DE CLIENT LABELT, ÉÉN KEER
 *
 * De RPC stuurt `id` én `label` mee. Omdat het id er al is, kan de client het
 * label zelf afleiden — en dan is er één algoritme in plaats van twee. De
 * serverkant blijft zijn label sturen (dat is de garantie dat er nooit een
 * naam over de lijn gaat), maar wat een beheerder ZIET komt overal hier
 * vandaan. Het alternatief — de client SHA-256 laten spiegelen — vraagt
 * `crypto.subtle`, en dat is asynchroon in een render-pad dat synchroon is.
 *
 * DIT IS GEEN ANONIMISERING. Met toegang tot de database is het id terug te
 * vinden. Het is een weergavebeperking: de naam staat er, hij wordt alleen niet
 * getoond zonder break glass.
 */

/**
 * Deterministische string-hash → klein positief geheel getal.
 *
 * Ook gebruikt als stabiele pseudo-tijdsoffset voor sessies zonder echte
 * `lastSeen`, zodat een rij niet bij elke render op een andere "tijd" springt.
 */
export function stableHash(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = h * 31 + input.charCodeAt(i) | 0;
  }
  return h >>> 0;
}

/** Vast voorvoegsel, zodat een pseudoniem nooit voor een naam kan doorgaan. */
const PSEUDONYM_PREFIX = 'L-';

/**
 * Het pseudoniem van één leerling. Zelfde id → zelfde label, overal in de app.
 *
 * Voeg hier geen variant toe (korter, langer, ander voorvoegsel) zonder de
 * bestaande te vervangen: twee vormen naast elkaar brengt exact het probleem
 * terug dat deze module oplost.
 */
export function pseudonymFor(learnerId: string): string {
  return `${PSEUDONYM_PREFIX}${stableHash(learnerId).toString(36).toUpperCase().slice(0, 5)}`;
}
